import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";
import { Sparkle, TrendUp, Users, CurrencyDollar, Phone, PhoneCall, CaretDown, CheckCircle, Warning } from "@phosphor-icons/react";

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <Icon size={16} weight="bold" /> {label}
    </div>
    <div className="mt-2 font-display text-3xl font-semibold">{value}</div>
  </div>
);

export default function AdminAISales() {
  const { data: rev, isLoading } = useQuery({ queryKey: ["ai-sales-revenue"], queryFn: async () => (await api.get("/ai-sales/revenue")).data, refetchInterval: 15000 });
  const { data: leadsData, isLoading: leadsLoading } = useQuery({ queryKey: ["ai-sales-leads"], queryFn: async () => (await api.get("/ai-sales/leads")).data });
  const leads = leadsData?.leads || [];
  const recent = rev?.recent || [];
  const M = (c) => (isLoading ? "…" : money(c || 0));

  return (
    <div className="space-y-6" data-testid="admin-ai-sales">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Sparkle size={24} weight="fill" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">SI Sales · Revenue Command</h1>
          <p className="text-sm text-muted-foreground">Real, SI-attributed wallet revenue and live pipeline.</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-today" label="Revenue Today" value={M(rev?.revenue_today_cents)} icon={CurrencyDollar} />
        <Stat testid="stat-month" label="Revenue (30d)" value={M(rev?.revenue_month_cents)} icon={TrendUp} />
        <Stat testid="stat-all" label="Total SI Revenue" value={M(rev?.revenue_all_cents)} icon={CurrencyDollar} />
        <Stat testid="stat-pipeline" label="Open / Won" value={isLoading ? "…" : `${rev?.open_conversations || 0} / ${rev?.won_conversations || 0}`} icon={Users} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Recent SI-closed sales</h2>
          <div className="mt-4 space-y-2" data-testid="ai-sales-recent">
            {recent.length === 0 && <p className="text-sm text-muted-foreground">{isLoading ? "Loading…" : "No SI sales yet — they'll appear here the moment a member pays."}</p>}
            {recent.map((r, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-accent/50 px-3 py-2 text-sm">
                <span className="truncate">{r.email || "member"}</span>
                <span className="font-semibold text-emerald-500">{money(r.amount_cents)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Top leads (by score)</h2>
          <div className="mt-4 space-y-2" data-testid="ai-sales-leads">
            {leads.length === 0 && <p className="text-sm text-muted-foreground">{leadsLoading ? "Loading…" : "No leads yet."}</p>}
            {leads.slice(0, 12).map((l, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-accent/50 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{l.name || l.email}</p>
                  <p className="truncate text-xs text-muted-foreground">{l.stage} · {l.next_best_action}</p>
                </div>
                <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">{l.lead_score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <VoiceSeller />
    </div>
  );
}

function VoiceSeller() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ telnyx_live: false, products: [], voices: [], inbound_webhook_url: "", default_from: "" });
  const [calls, setCalls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dial, setDial] = useState({ to: "", product_key: "" });
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: cl }] = await Promise.all([
        api.get("/voice-sales/config"), api.get("/voice-sales/calls"),
      ]);
      setCfg(c.config);
      setMeta({ telnyx_live: c.telnyx_live, products: c.products || [], voices: c.voices || [], inbound_webhook_url: c.inbound_webhook_url, default_from: c.default_from });
      setCalls(cl.calls || []);
      if (!dial.product_key && (c.products || []).length) setDial((d) => ({ ...d, product_key: c.products[0].key }));
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); const t = setInterval(() => api.get("/voice-sales/calls").then(({ data }) => setCalls(data.calls || [])).catch(() => {}), 8000); return () => clearInterval(t); }, []);

  const saveCfg = async (patch) => {
    setBusy(true);
    try { await api.post("/voice-sales/config", patch); setCfg((c) => ({ ...c, ...patch })); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const placeCall = async () => {
    if (!dial.to.startsWith("+")) { toast.error("Enter the number in +E.164 format, e.g. +15551234567"); return; }
    setBusy(true);
    try { await api.post("/voice-sales/call", dial); toast.success("Calling… the SI seller is dialing now."); setTimeout(load, 1500); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="py-10 text-center text-muted-foreground" data-testid="voice-seller-loading">Loading Voice Seller…</div>;

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="voice-seller-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary"><PhoneCall size={22} weight="fill" /></div>
          <div>
            <h2 className="font-display text-xl font-semibold">SI Voice Seller</h2>
            <p className="text-sm text-muted-foreground">Inbound + outbound SI sales calls — same catalog, brain and CRM as chat.</p>
          </div>
        </div>
        <span data-testid="voice-telnyx-badge" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${meta.telnyx_live ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>
          {meta.telnyx_live ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
          Telnyx {meta.telnyx_live ? "LIVE" : "not live"}
        </span>
      </div>

      {/* config */}
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-background p-4">
          <div><h3 className="font-semibold">Enable SI Voice Seller</h3><p className="text-sm text-muted-foreground">Master switch for SI sales calling (inbound + outbound).</p></div>
          <input data-testid="voice-enabled-toggle" type="checkbox" checked={!!cfg.voice_sales_enabled} disabled={busy}
            onChange={(e) => saveCfg({ voice_sales_enabled: e.target.checked })} className="mt-1 h-5 w-9 accent-primary" />
        </div>
        <div className="flex items-start justify-between gap-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div><h3 className="font-semibold">Autonomous calling (hands-free)</h3><p className="text-sm text-muted-foreground">Let the SI call qualified, consented hot leads on its own — no dialing. Capped per day; opt-outs honoured.</p></div>
          <input data-testid="voice-autonomous-toggle" type="checkbox" checked={!!cfg.voice_sales_autonomous} disabled={busy || !cfg.voice_sales_enabled}
            onChange={(e) => saveCfg({ voice_sales_autonomous: e.target.checked })} className="mt-1 h-5 w-9 accent-emerald-500" />
        </div>
        <Field label="Max autonomous calls per day" testid="voice-maxperday-input" value={String(cfg.voice_sales_max_per_day ?? 20)}
          placeholder="20" onSave={(v) => saveCfg({ voice_sales_max_per_day: Number(v) || 0 })} />
        <Field label="SI Voice number (inbound DID, +E.164)" testid="voice-did-input" value={cfg.voice_sales_did}
          placeholder={meta.default_from || "+15551234567"} onSave={(v) => saveCfg({ voice_sales_did: v })} />
        <Field label="Inbound greeting (spoken)" testid="voice-greeting-input" value={cfg.voice_sales_greeting}
          onSave={(v) => saveCfg({ voice_sales_greeting: v })} />
        <Field label="Human handoff number (+E.164, optional)" testid="voice-handoff-input" value={cfg.voice_sales_handoff_number}
          placeholder="+15559876543" onSave={(v) => saveCfg({ voice_sales_handoff_number: v })} />
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Voice</span>
          <select data-testid="voice-voice-select" value={cfg.voice_sales_voice || ""} disabled={busy}
            onChange={(e) => saveCfg({ voice_sales_voice: e.target.value })}
            className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary">
            {meta.voices.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
        </label>
        <div className="rounded-xl border border-white/10 bg-background p-4 text-sm">
          <p className="text-xs font-medium text-muted-foreground">Point your Telnyx number's inbound voice webhook here</p>
          <code data-testid="voice-inbound-url" className="mt-1 block break-all text-xs text-primary">{meta.inbound_webhook_url}</code>
        </div>
      </div>

      {/* outbound dialer */}
      <div className="mt-6 rounded-xl border border-white/10 bg-background p-4">
        <h3 className="mb-3 flex items-center gap-2 font-semibold"><Phone size={16} weight="fill" /> Place an outbound SI call</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="min-w-[200px] flex-1">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Phone number (+E.164)</span>
            <input data-testid="voice-dial-number" value={dial.to} onChange={(e) => setDial({ ...dial, to: e.target.value })}
              placeholder="+15551234567" className="w-full rounded-xl border border-white/10 bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
          </label>
          <label className="min-w-[200px] flex-1">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Product to pitch</span>
            <select data-testid="voice-dial-product" value={dial.product_key} onChange={(e) => setDial({ ...dial, product_key: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-card px-3 py-2 text-sm outline-none focus:border-primary">
              {meta.products.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </label>
          <button data-testid="voice-place-call" onClick={placeCall} disabled={busy || !cfg.voice_sales_enabled}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            Call now
          </button>
        </div>
        {!cfg.voice_sales_enabled && <p className="mt-2 text-xs text-amber-500">Enable the SI Voice Seller above to place calls.</p>}
      </div>

      {/* call log */}
      <div className="mt-6">
        <h3 className="mb-3 font-semibold">Recent calls</h3>
        <div className="space-y-2" data-testid="voice-calls-list">
          {calls.length === 0 && <p className="text-sm text-muted-foreground">No calls yet.</p>}
          {calls.map((c) => (
            <div key={c.id} data-testid={`voice-call-row-${c.id}`} className="rounded-xl border border-white/10 bg-background">
              <button onClick={() => setOpen(open === c.id ? null : c.id)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {c.direction === "inbound" ? "⬇ Inbound" : "⬆ Outbound"} · {c.direction === "inbound" ? c.from : c.to} {c.product_key ? `· ${c.product_key}` : ""}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{(c.created_at || "").slice(0, 16).replace("T", " ")} · {(c.turns || []).length} turns</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${dispClass(c.disposition || c.status)}`}>{c.disposition || c.status}</span>
                  <CaretDown size={16} className={`transition-transform ${open === c.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {open === c.id && (
                <div className="border-t border-white/5 px-4 py-3">
                  {c.error && <p className="mb-2 text-xs text-destructive">Error: {c.error}</p>}
                  <div className="space-y-1.5">
                    {(c.turns || []).map((t, i) => (
                      <div key={i} className={`text-sm ${t.role === "agent" ? "text-primary" : ""}`}>
                        <span className="text-xs font-semibold uppercase text-muted-foreground">{t.role === "agent" ? "SI" : "Caller"}: </span>{t.text}
                      </div>
                    ))}
                    {(c.turns || []).length === 0 && <p className="text-sm text-muted-foreground">No conversation captured yet.</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function dispClass(d) {
  const s = (d || "").toLowerCase();
  if (s.includes("convert") || s === "won") return "bg-emerald-500/15 text-emerald-500";
  if (s.includes("handoff")) return "bg-sky-500/15 text-sky-500";
  if (s === "failed") return "bg-destructive/15 text-destructive";
  if (s === "ringing" || s === "dialing" || s === "in_progress" || s === "answered") return "bg-amber-500/15 text-amber-500";
  return "bg-white/5 text-muted-foreground";
}

function Field({ label, value, onSave, testid, placeholder }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <input data-testid={testid} defaultValue={value} key={`${testid}-${value}`} placeholder={placeholder}
        onBlur={(e) => { if (e.target.value !== (value || "")) onSave(e.target.value); }}
        className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
    </label>
  );
}
